import { Box, Paper, Typography } from "@mui/material";
import { MciMoreInfo, MciDomainPct } from "../api/scans";

// CognitiveReport — «Tap for full report» панель врача: React-порт
// CogNimbus/cognimeter/render_more_info.py (спека — cognimeter/MORE_INFO_SPEC.md).
// Вход — response.more_info: радар когнитивного профиля (percentile vs healthy
// peers) + диаграмма драйверов (SHAP, свёрнутый в клинические конструкты).
// Геометрия/цвета — source of truth в render_more_info.py; здесь чистое view,
// никаких пересчётов модели. Patient-facing подписи переопределены в сентябре
// 2026 для Class II / PTY-exemption presentation: MCI/NC сокращения заменены
// недиагностическими формулировками. Оригинальный handoff-дроп с доправочным
// текстом лежит в CogNimbus_v2/ (его не трогаем).

const TEAL = "#157F76";
const DARK = "#28323F";
const GRAY = "#646E78";
const RED = "#D61C20";
const GREEN = "#2FA84A";
const NCCOL = "#0E6B3E";
const IMPAIRMENT_COL = "#9E1418";

const zoneColor = (pct: number, ncPct: number, mciPct: number) =>
  pct >= ncPct ? GREEN : pct <= mciPct ? RED : GRAY;

// 1st / 2nd / 3rd / 4th — в python-рендере всегда "th" ("2th pct"), здесь
// исправлено: подписи renderer-only, на данные не влияют (MORE_INFO_SPEC §1)
const ordinal = (n: number) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
};

// ── Радар: 3 оси-домена, зоны NC / borderline / MCI + полигон пациента ──────
function ProfileRadar({
  domains,
  refNc,
  refMci,
}: {
  domains: MciDomainPct[];
  refNc: MciDomainPct[];
  refMci: MciDomainPct[];
}) {
  const CX = 150, CY = 150, R = 130;
  const n = domains.length;
  const ang = (i: number) => -90 + (i * 360) / n;
  const pt = (a: number, r: number): [number, number] => [
    CX + r * Math.cos((a * Math.PI) / 180),
    CY + r * Math.sin((a * Math.PI) / 180),
  ];

  const ncMap: Record<string, number> = {};
  refNc.forEach((x) => { ncMap[x.domain] = x.pct; });
  const mciMap: Record<string, number> = {};
  refMci.forEach((x) => { mciMap[x.domain] = x.pct; });

  const rNc = domains.map((d) => (ncMap[d.domain] ?? 50) / 100);
  const rMci = domains.map((d) => (mciMap[d.domain] ?? 20) / 100);
  const one = domains.map(() => 1);

  const polyd = (fr: number[]) =>
    "M " +
    fr.map((f, i) => {
      const [x, y] = pt(ang(i), R * f);
      return `${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(" L ") +
    " Z";

  const patientPoints = domains
    .map((d, i) => {
      const [x, y] = pt(ang(i), (R * d.pct) / 100);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const LX = 214, LY = 10; // легенда зон (правый верхний угол)

  return (
    <svg viewBox="-52 -12 404 282" style={{ width: "100%", height: "auto" }}>
      {/* зоны: снаружи NC-медианы — зелёная, между — серая, внутри MCI-медианы — красная */}
      <path d={`${polyd(one)} ${polyd(rNc)}`} fillRule="evenodd" fill={GREEN} fillOpacity={0.15} />
      <path d={`${polyd(rNc)} ${polyd(rMci)}`} fillRule="evenodd" fill={GRAY} fillOpacity={0.2} />
      <path d={polyd(rMci)} fill={RED} fillOpacity={0.15} />

      <path d={polyd(one)} fill="none" stroke="#D3DBDE" strokeWidth={1} />
      {domains.map((_, i) => {
        const [x, y] = pt(ang(i), R);
        return <line key={i} x1={CX} y1={CY} x2={x.toFixed(1)} y2={y.toFixed(1)} stroke="#D3DBDE" />;
      })}

      <path d={polyd(rNc)} fill="none" stroke={NCCOL} strokeWidth={1.1} strokeDasharray="4 3" />
      <path d={polyd(rMci)} fill="none" stroke={IMPAIRMENT_COL} strokeWidth={1.1} strokeDasharray="4 3" />

      <polygon points={patientPoints} fill={`${TEAL}30`} stroke={TEAL} strokeWidth={1.5} />
      {domains.map((d, i) => {
        const [x, y] = pt(ang(i), (R * d.pct) / 100);
        return <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r={2.5} fill={TEAL} />;
      })}

      {/* подписи осей: название домена (может быть в 2 строки) + перцентиль */}
      {domains.map((d, i) => {
        const a = ang(i);
        const [lx, ly] = pt(a, R + 20);
        const cos = Math.cos((a * Math.PI) / 180);
        const anchor = cos > 0.3 ? "start" : cos < -0.3 ? "end" : "middle";
        const parts = d.domain.split(" & ");
        const col = zoneColor(d.pct, ncMap[d.domain] ?? 50, mciMap[d.domain] ?? 20);
        return (
          <g key={i}>
            {parts.map((part, j) => (
              <text
                key={j}
                x={lx.toFixed(0)}
                y={(ly + 11 * j).toFixed(0)}
                fontSize={9.5}
                fontWeight={600}
                fill={TEAL}
                textAnchor={anchor}
              >
                {j < parts.length - 1 ? `${part} &` : part}
              </text>
            ))}
            <text
              x={lx.toFixed(0)}
              y={(ly + 11 * parts.length).toFixed(0)}
              fontSize={9.5}
              fill={col}
              textAnchor={anchor}
            >
              {ordinal(d.pct)} pct
            </text>
          </g>
        );
      })}

      {/* легенда зон */}
      <rect x={LX - 14} y={LY - 7} width={142} height={80} rx={6} fill="#ffffff" fillOpacity={0.85} stroke="#E4EAEC" strokeWidth={0.8} />
      <text x={LX} y={LY + 1} fontSize={9} fontWeight={700} fill={GRAY} letterSpacing="0.2">PROFILE ZONES</text>
      {([[GREEN, 0.45, "normal cognition"], [GRAY, 0.6, "borderline"], [RED, 0.45, "cognitive impairment"]] as [string, number, string][]).map(
        ([col, op, lab], k) => {
          const yy = LY + 11 + k * 11;
          return (
            <g key={lab}>
              <rect x={LX} y={yy} width={10} height={8} rx={1.5} fill={col} fillOpacity={op} stroke={col} strokeWidth={0.5} />
              <text x={LX + 15} y={yy + 7} fontSize={8.2} fill={DARK}>{lab}</text>
            </g>
          );
        }
      )}
      {([[NCCOL, "normal reference"], [IMPAIRMENT_COL, "impairment reference"]] as [string, string][]).map(([col, lab], k) => {
        const yy = LY + 11 + (3 + k) * 11;
        return (
          <g key={lab}>
            <line x1={LX} y1={yy + 4} x2={LX + 10} y2={yy + 4} stroke={col} strokeWidth={1.3} strokeDasharray="2.5 1.6" />
            <text x={LX + 15} y={yy + 7} fontSize={8.2} fill={DARK}>{lab}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Драйверы: расходящиеся бары (слева protective, справа toward impairment) ─
function DriverBars({ drivers }: { drivers: { label: string; risk: boolean; share: number }[] }) {
  const WIDTH = 500, ROWH = 42, BARH = 15, HALF = 185, FS = 11;
  const cx = WIDTH / 2, top = 48;
  const maxShare = drivers.length ? Math.max(...drivers.map((d) => d.share)) : 1;
  const axMax = Math.max(20, Math.ceil(maxShare / 10) * 10);
  const H = top + drivers.length * ROWH + 20;
  const xof = (share: number) => cx + (share / axMax) * HALF;

  const ticks: number[] = [];
  for (let t = 0; t <= axMax; t += 10) ticks.push(t);

  return (
    <svg viewBox={`0 0 ${WIDTH} ${H}`} style={{ width: "100%", height: "auto" }}>
      <text x={cx - 24} y={14} fontSize={11} fill={GREEN} textAnchor="end" fontWeight={600}>
        {"normal cognition ◀"}
      </text>
      <text x={cx} y={14} fontSize={11} fill={DARK} textAnchor="middle" fontWeight={600}>toward</text>
      <text x={cx + 24} y={14} fontSize={11} fill={RED} textAnchor="start" fontWeight={600}>
        {"▶ cognitive impairment"}
      </text>

      {ticks.map((t) =>
        (t === 0 ? [0] : [-1, 1]).map((sgn) => {
          const xx = xof(sgn * t);
          return (
            <g key={`${t}-${sgn}`}>
              <line
                x1={xx.toFixed(1)} y1={32} x2={xx.toFixed(1)} y2={H - 16}
                stroke={t === 0 ? "#C9D2D6" : "#EDF0F2"} strokeWidth={t === 0 ? 1.4 : 1}
              />
              <text
                x={xx.toFixed(1)} y={H - 4} fontSize={9} fill={GRAY} textAnchor="middle"
                fontFamily="ui-monospace,Menlo,monospace"
              >
                {t}
              </text>
            </g>
          );
        })
      )}

      {drivers.map((d, i) => {
        const y = top + i * ROWH;
        const col = d.risk ? RED : GREEN;
        const x2 = xof(d.risk ? d.share : -d.share);
        return d.risk ? (
          <g key={d.label}>
            <rect x={cx.toFixed(1)} y={y} width={(x2 - cx).toFixed(1)} height={BARH} rx={3} fill={col} />
            <text x={cx + 6} y={y - 4} fontSize={FS} fill={DARK}>{d.label}</text>
            <text
              x={(x2 + 4).toFixed(1)} y={y + BARH - 3} fontSize={10} fill={col} fontWeight={600}
              fontFamily="ui-monospace,Menlo,monospace"
            >
              {Math.round(d.share)}
            </text>
          </g>
        ) : (
          <g key={d.label}>
            <rect x={x2.toFixed(1)} y={y} width={(cx - x2).toFixed(1)} height={BARH} rx={3} fill={col} />
            <text x={cx - 6} y={y - 4} fontSize={FS} fill={DARK} textAnchor="end">{d.label}</text>
            <text
              x={(x2 - 4).toFixed(1)} y={y + BARH - 3} fontSize={10} fill={col} fontWeight={600}
              textAnchor="end" fontFamily="ui-monospace,Menlo,monospace"
            >
              {Math.round(d.share)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{ fontSize: 15, letterSpacing: ".5px", textTransform: "uppercase", color: TEAL, fontWeight: 700, mb: 1.5 }}
  >
    {children}
  </Typography>
);

const Legend = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ color: GRAY, fontSize: 17, lineHeight: 1.6, mt: 2, textAlign: "center" }}>
    <Box
      component="ul"
      sx={{ display: "inline-block", textAlign: "left", m: 0, pl: 3, listStyle: "disc", maxWidth: 700 }}
    >
      {children}
    </Box>
  </Box>
);

export default function CognitiveReport({ moreInfo }: { moreInfo: MciMoreInfo }) {
  const domains = moreInfo.domain_profile || [];
  const refNc = moreInfo.ref_nc || [];
  const refMci = moreInfo.ref_mci || [];
  const drivers = (moreInfo.drivers || []).map((d) => ({
    label: d.construct,
    risk: d.direction === "toward_impairment",
    share: d.share,
  }));

  const canRadar = domains.length >= 3 && refNc.length > 0 && refMci.length > 0;

  return (
    <>
      {canRadar && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <CardTitle>Cognitive profile</CardTitle>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ProfileRadar domains={domains} refNc={refNc} refMci={refMci} />
          </Box>
          <Legend>
            <li><b>Each of the three axes is one cognitive domain.</b></li>
            <li>
              <svg viewBox="0 0 18 16" style={{ display: "inline-block", width: 18, height: 16, verticalAlign: -3, marginRight: 5 }}>
                <polygon points="9,2.5 16,13.5 2,13.5" fill={TEAL} fillOpacity={0.18} stroke={TEAL} strokeWidth={1.3} />
                <circle cx="9" cy="2.5" r="1.7" fill={TEAL} />
                <circle cx="16" cy="13.5" r="1.7" fill={TEAL} />
                <circle cx="2" cy="13.5" r="1.7" fill={TEAL} />
              </svg>
              this patient
            </li>
            <li>Outside the <b style={{ color: NCCOL }}>green dashed triangle</b>: performance trends toward the normal-cognition range.</li>
            <li>Inside the <b style={{ color: IMPAIRMENT_COL }}>red dashed triangle</b>: performance trends toward the cognitive-impairment range.</li>
            <li>
              <b style={{ color: GRAY }}>Gray band</b> between red and green triangles = borderline.
            </li>
            <li>This profile is an assessment aid and does not indicate a clinical diagnosis.</li>
          </Legend>
        </Paper>
      )}

      {drivers.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <CardTitle>Key cognitive abilities driving this score</CardTitle>
          <DriverBars drivers={drivers} />
          <Legend>
            <li>Bar size = each ability's share of this patient's score (%).</li>
            <li>
              {drivers.length} cognitive abilities account for ~{moreInfo.driver_coverage}% of the score
              for this patient.
            </li>
          </Legend>
        </Paper>
      )}
    </>
  );
}
